import { Eye, Folder as FolderIcon, FolderPlus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FolderBreadcrumbs } from '../components/folders';
import { FolderActionsModal } from '../components/folders/FolderActionsModal';
import { FolderModal } from '../components/folders/FolderModal';
import { useAuth } from '../contexts/AuthContext';
import {
	useCreateNestedFolder,
	useDeleteNestedFolder,
	useFolderChildren,
	useFolderTree,
} from '../hooks/useFolderTree';
import { useFolder } from '../hooks/useFolders';
import type { Folder } from '../types/api';

/**
 * Enhanced FoldersPage with nested folder tree view
 */
export function FoldersPage() {
	const { folderId } = useParams<{ folderId: string }>();
	const { canWrite } = useAuth();
	const navigate = useNavigate();
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [actionModal, setActionModal] = useState<{
		isOpen: boolean;
		action: 'createSubfolder' | 'delete';
		folder?: Folder;
	}>({ isOpen: false, action: 'createSubfolder' });

	// Current folder data
	const { data: currentFolder } = useFolder(folderId || '');
	const { data: folderTree } = useFolderTree();
	const { data: children } = useFolderChildren(folderId || '');

	// Flatten the tree to show all folders with their depth
	const flattenFolders = (folders: Folder[], depth = 0): Array<Folder & { depth: number }> => {
		const result: Array<Folder & { depth: number }> = [];
		for (const folder of folders || []) {
			result.push({ ...folder, depth });
			if (folder.children && folder.children.length > 0) {
				result.push(...flattenFolders(folder.children, depth + 1));
			}
		}
		return result;
	};

	// Build proper hierarchy paths from tree structure
	const buildFolderPath = (folderId: string, tree: Folder[]): string => {
		const findPath = (
			folders: Folder[],
			targetId: string,
			currentPath: string[] = []
		): string[] | null => {
			for (const folder of folders) {
				const newPath = [...currentPath, folder.name];
				if (folder.id === targetId) {
					return newPath;
				}
				if (folder.children && folder.children.length > 0) {
					const found = findPath(folder.children, targetId, newPath);
					if (found) return found;
				}
			}
			return null;
		};

		const path = findPath(tree, folderId);
		return path ? path.join(' / ') : 'Root';
	};

	// Show different content based on whether we're at root or in a specific folder
	const foldersToShow = folderId
		? flattenFolders(children || [], 0).map(folder => ({
				...folder,
				fullPath: buildFolderPath(folder.id, folderTree || []),
		  })) // Complete hierarchy starting from current folder
		: flattenFolders(folderTree || []).map(folder => ({
				...folder,
				fullPath: buildFolderPath(folder.id, folderTree || []),
		  })); // Complete hierarchy at root with correct paths

	// Mutations
	const createNestedFolder = useCreateNestedFolder();
	const deleteNestedFolder = useDeleteNestedFolder();

	/**
	 * Handle folder selection from tree
	 */
	const handleFolderSelect = (folder: Folder) => {
		// Navigate to the folder using React Router
		navigate(`/folders/${folder.id}`);
	};

	const handleViewWorkflows = (folderId: string) => {
		// Navigate to flows page with folder filter (correct route is /flows, not /workflows)
		navigate(`/flows?folder=${folderId}`);
	};

	/**
	 * Handle creating a new folder
	 */
	const handleCreateFolder = async (name: string) => {
		try {
			await createNestedFolder.mutateAsync({
				name,
				parentId: folderId,
			});
			setShowCreateModal(false);
		} catch (error) {
			console.error('Failed to create folder:', error);
			// TODO: Show error toast
		}
	};

	/**
	 * Handle creating a subfolder from table action
	 */
	const handleCreateSubfolder = (parentFolder: Folder) => {
		setActionModal({
			isOpen: true,
			action: 'createSubfolder',
			folder: parentFolder,
		});
	};

	/**
	 * Handle deleting a folder with safety checks
	 */
	const handleDeleteFolder = (folder: Folder) => {
		setActionModal({
			isOpen: true,
			action: 'delete',
			folder: folder,
		});
	};

	/**
	 * Handle modal actions
	 */
	const handleModalAction = async (data: any) => {
		if (!actionModal.folder) return;

		try {
			if (actionModal.action === 'createSubfolder') {
				await createNestedFolder.mutateAsync({
					name: data.name,
					parentId: actionModal.folder.id,
				});
			} else if (actionModal.action === 'delete') {
				await deleteNestedFolder.mutateAsync({
					folderId: actionModal.folder.id,
					moveChildrenToRoot: data.option === 'move',
				});
			}
			setActionModal({ isOpen: false, action: 'createSubfolder' });
		} catch (error) {
			console.error('Action failed:', error);
			// TODO: Show error toast
		}
	};

	return (
		<div className="flex h-full bg-gray-50 overflow-hidsden dark:bg-gray-900">
			{/* Main Content Area */}
			<main className="overflow-auto flex-1">
				{/* Header with Breadcrumbs and Actions */}
				<header className="sticky top-0 z-10 px-6 py-4 bg-white border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
					<div className="flex justify-between items-center">
						<div className="flex-1">
							<FolderBreadcrumbs
								currentFolderId={folderId}
								currentFolder={currentFolder}
								basePath="/folders"
								className="mb-2"
							/>
							<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
								{currentFolder ? currentFolder.name : 'All Folders'}
							</h1>
							{currentFolder?.description && (
								<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
									{currentFolder.description}
								</p>
							)}
						</div>

						{/* Actions */}
						<div className="flex gap-3 items-center">
							{canWrite() && (
								<>
									<button
										onClick={() => setShowCreateModal(true)}
										className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm transition-colors dark:border-gray-600 dark:text-gray-200 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
										title="Create a new subfolder"
									>
										<FolderPlus className="mr-2 w-4 h-4" />
										New Folder
									</button>
								</>
							)}
						</div>
					</div>
				</header>
				{/* Content */}
				<div className="p-6 space-y-6">
					{/* Dropbox-Style Table */}
					<div className="overflow-hidden bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
						<table className="w-full text-sm">
							<thead className="text-left text-gray-400 bg-gray-50 border-b border-gray-200 dark:text-gray-500 dark:border-gray-700 dark:bg-gray-900">
								<tr>
									<th className="px-4 py-3 font-medium">Name</th>
									<th className="px-4 py-3 font-medium">Workflows</th>
									<th className="px-4 py-3 font-medium">Folder Path</th>
									<th className="px-4 py-3 font-medium">Who can access</th>
									<th className="px-4 py-3 font-medium">Modified</th>
									<th className="px-4 py-3 font-medium">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 dark:divide-gray-700">
								{/* Folders with appropriate scope */}
								{foldersToShow?.map(folder => (
									<tr
										key={folder.id}
										onDoubleClick={() => handleFolderSelect(folder)}
										className="transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
										title={`Double-click to open ${folder.name}`}
									>
										<td className="px-4 py-3">
											<div
												className="flex gap-3 items-center"
												style={{ paddingLeft: `${folder.depth * 24}px` }}
											>
												{folder.depth > 0 && (
													<span className="text-gray-400 dark:text-gray-500">
														{'└─ '.repeat(1)}
													</span>
												)}
												<FolderIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" />
												<span className="font-medium text-gray-900 dark:text-white">
													{folder.name}
												</span>
												{folder.depth > 0 && (
													<span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
														Level {folder.depth}
													</span>
												)}
											</div>
										</td>
										<td className="px-4 py-3 text-gray-600 dark:text-gray-400">
											<div className="flex gap-2 items-center">
												<span className="font-medium text-gray-900 dark:text-white">
													{folder.flowCount || 0}
												</span>
												{folder.flowCount > 0 && (
													<button
														onClick={e => {
															e.stopPropagation();
															handleViewWorkflows(folder.id);
														}}
														className="inline-flex gap-1 items-center px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded transition-colors dark:text-blue-400 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30"
														title="View workflows in this folder"
													>
														<Eye className="w-3 h-3" />
														View workflows
													</button>
												)}
											</div>
										</td>
										<td className="px-4 py-3 text-gray-600 dark:text-gray-400">
											<span className="text-sm" title={folder.fullPath}>
												{folder.fullPath || 'Root'}
											</span>
										</td>
										<td className="px-4 py-3 text-gray-600 dark:text-gray-400">
											Only you
										</td>
										<td className="px-4 py-3 text-gray-600 dark:text-gray-400">
											{new Date(folder.updatedAt).toLocaleDateString()}
										</td>
										<td className="px-4 py-3">
											<div className="flex gap-2 items-center">
												{canWrite() && (
													<button
														onClick={e => {
															e.stopPropagation();
															handleCreateSubfolder(folder);
														}}
														className="p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
														title="Create subfolder"
													>
														+ Sub
													</button>
												)}
												{canWrite() && (
													<button
														onClick={e => {
															e.stopPropagation();
															handleDeleteFolder(folder);
														}}
														className="p-1 text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
														title="Delete folder"
													>
														Delete
													</button>
												)}
											</div>
										</td>
									</tr>
								))}

								{/* No items message */}
								{(!foldersToShow || foldersToShow.length === 0) && (
									<tr>
										<td
											colSpan={5}
											className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
										>
											No folders yet - create your first folder to get started
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</main>

			{/* Create Folder Modal */}
			<FolderModal
				isOpen={showCreateModal}
				mode="create"
				isSubmitting={createNestedFolder.isPending}
				onClose={() => setShowCreateModal(false)}
				onSubmit={handleCreateFolder}
			/>

			{/* Folder Actions Modal */}
			<FolderActionsModal
				isOpen={actionModal.isOpen}
				onClose={() => setActionModal({ isOpen: false, action: 'createSubfolder' })}
				folder={actionModal.folder!}
				action={actionModal.action}
				onConfirm={handleModalAction}
				isLoading={createNestedFolder.isPending || deleteNestedFolder.isPending}
			/>
		</div>
	);
}

export default FoldersPage;
